import { drawing } from '@progress/kendo-drawing';
import { Cursors, HIT_TEST_DISTANCE, MOUSE_ENTER, MOUSE_LEAVE, ZOOM_END, ZOOM_START } from './../../constants';
import { deepExtend } from './../../utils/deep-extend';
import { ConnectionEditTool } from './ConnectionEditTool';
import { ConnectionResizeTool } from './ConnectionResizeTool';
import { ConnectionTool } from './ConnectionTool';
import { PointerTool } from './PointerTool';
import { ScrollerTool } from './ScrollerTool';
import { SelectionTool } from './SelectionTool';
import { testKey } from './testKey';
import { Connection } from './../../dom/Connection';
import { Rect } from './../../math';
import { inArray } from './../../utils';
const round = drawing.util.round;
function hitTestShapeConnectors(shape, point) {
    let connector, position, rect;
    for (let idx = 0; idx < shape.connectors.length; idx++) {
        connector = shape.connectors[idx];
        position = connector.position();
        rect = new Rect(position.x, position.y);
        rect.inflate(HIT_TEST_DISTANCE, HIT_TEST_DISTANCE);
        if (rect.contains(point)) {
            return connector;
        }
    }
}
/**
 * The service managing the tools.
 *
 * @type {*}
 */
export class ToolService {
    /**
     * Creates a new ToolService instance.
     * @param diagram The diagram instance that this tool service will manage
     */
    constructor(diagram) {
        this.diagram = diagram;
        this.tools = [
            new ScrollerTool(this),
            new ConnectionResizeTool(this),
            new ConnectionEditTool(this),
            new ConnectionTool(this),
            new SelectionTool(this),
            new PointerTool(this)
        ]; // the order matters.
        this.activeTool = undefined;
    }
    /**
     * Starts a tool interaction at the specified point.
     * @param p The point where the interaction starts
     * @param meta Metadata about the interaction (e.g., keyboard modifiers)
     * @param nativeEvent The native DOM event that triggered this interaction
     * @returns Always returns true to indicate the event was handled
     */
    start(p, meta, nativeEvent) {
        meta = deepExtend({}, meta);
        if (this.activeTool) {
            this.activeTool.end(p, meta);
        }
        this._updateHoveredItem(p, meta, nativeEvent);
        this._activateTool(p, meta);
        this.activeTool.start(p, meta, nativeEvent);
        this._updateCursor(p);
        this.diagram.focus();
        this.diagram.canvas.surface.suspendTracking();
        this.startPoint = p;
        return true;
    }
    /**
     * Handles mouse movement during tool interaction.
     * @param p The current mouse position
     * @param meta Metadata about the interaction (e.g., keyboard modifiers)
     * @param nativeEvent The native DOM event that triggered this movement
     * @returns Always returns true to indicate the event was handled
     */
    move(p, meta, nativeEvent) {
        meta = deepExtend({}, meta);
        let updateHovered = true;
        if (this.activeTool) {
            updateHovered = this.activeTool.move(p, meta, nativeEvent);
        }
        if (updateHovered) {
            this._updateHoveredItem(p, meta, nativeEvent);
        }
        this._updateCursor(p);
        return true;
    }
    /**
     * Ends the current tool interaction.
     * @param point The point where the interaction ends
     * @param meta Metadata about the interaction (e.g., keyboard modifiers)
     * @param nativeEvent The native DOM event that triggered the end of interaction
     * @returns Always returns true to indicate the event was handled
     */
    end(point, meta, nativeEvent) {
        meta = deepExtend({}, meta);
        if (this.activeTool) {
            this.activeTool.end(point, meta, nativeEvent);
        }
        this.diagram.canvas.surface.resumeTracking();
        this.activeTool = undefined;
        this._updateCursor(point);
        return true;
    }
    /**
     * Handles keyboard input for diagram operations.
     * Processes keyboard shortcuts for actions like select all, undo, redo, copy, paste, etc.
     * @param key The key code of the pressed key
     * @param meta Metadata about the key event (e.g., ctrl, shift, alt modifiers)
     * @returns True if the key event was handled, undefined otherwise
     */
    keyDown(key, meta) {
        const diagram = this.diagram;
        meta = deepExtend({ ctrlKey: false, metaKey: false, altKey: false }, meta);
        if ((meta.ctrlKey || meta.metaKey) && !meta.altKey) { // ctrl or option
            if (testKey(key, 'a')) { // A: select all
                diagram.selectAll();
                diagram._destroyToolBar();
                return true;
            }
            else if (testKey(key, 'z')) { // Z: undo
                diagram.undo();
                diagram._destroyToolBar();
                return true;
            }
            else if (testKey(key, 'y')) { // y: redo
                diagram.redo();
                diagram._destroyToolBar();
                return true;
            }
            else if (testKey(key, 'c')) {
                diagram.copy();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'x')) {
                diagram.cut();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'v')) {
                diagram.paste();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'l')) {
                diagram.layout();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'd')) {
                diagram._destroyToolBar();
                diagram.copy();
                diagram.paste();
            }
        }
        else if (key === 46 || key === 8) { // del: deletion
            const toRemove = this.diagram._triggerRemove(diagram.select());
            if (toRemove.length) {
                this.diagram.remove(toRemove, true);
                this.diagram._syncChanges();
                this.diagram._destroyToolBar();
            }
            return true;
        }
        else if (key === 27) { // ESC: stop any action
            this._discardNewConnection();
            diagram.deselect();
            diagram._destroyToolBar();
            return true;
        }
    }
    /**
     * Handles mouse wheel events for diagram zooming.
     * @param p The point where the wheel event occurred
     * @param meta Metadata about the wheel event including delta value
     * @param nativeEvent The native wheel event
     * @returns Always returns true to indicate the event was handled
     */
    wheel(p, meta, nativeEvent) {
        const diagram = this.diagram;
        let z = diagram.zoom();
        const delta = meta.delta, options = diagram.options, zoomRate = options.zoomRate, zoomOptions = { point: p, meta: meta, zoom: z, nativeEvent };
        if (diagram.trigger(ZOOM_START, zoomOptions)) {
            return;
        }
        if (delta < 0) {
            z += zoomRate;
        }
        else {
            z -= zoomRate;
        }
        z = round(Math.max(options.zoomMin, Math.min(options.zoomMax, z)), 2);
        zoomOptions.zoom = z;
        diagram.zoom(z, zoomOptions);
        diagram.trigger(ZOOM_END, zoomOptions);
        return true;
    }
    /**
     * Sets a tool at the specified index in the tools array.
     * @param tool The tool instance to set
     * @param index The index position where to place the tool
     */
    setTool(tool, index) {
        tool.toolService = this;
        this.tools[index] = tool;
    }
    /**
     * Selects a single item in the diagram.
     * Handles selection logic based on selectable options and keyboard modifiers.
     * @param item The diagram item to select
     * @param meta Metadata about the selection event (e.g., ctrl key for multi-selection)
     */
    selectSingle(item, meta) {
        const diagram = this.diagram;
        const selectable = diagram.options.selectable;
        if (selectable && !item.isSelected && item.options.selectable !== false) {
            const addToSelection = meta.ctrlKey && selectable.multiple !== false;
            diagram.select(item, { addToSelection: addToSelection });
        }
    }
    /** @hidden */
    _discardNewConnection() {
        if (this.newConnection) {
            this.diagram.remove(this.newConnection);
            this.newConnection = undefined;
        }
    }
    /** @hidden */
    _activateTool(p, meta) {
        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            if (tool.tryActivate(p, meta)) {
                this.activeTool = tool;
                break; // activating the first available tool in the loop.
            }
        }
    }
    /** @hidden */
    _updateCursor(p) {
        const element = this.diagram.element;
        let cursor;
        if (this.activeTool) {
            cursor = this.activeTool.getCursor(p);
        }
        else if (this.hoveredAdorner) {
            cursor = this.hoveredAdorner._getCursor(p);
            // If adorner returns undefined, fall back to hovered item
            if (!cursor && this.hoveredItem) {
                cursor = this.hoveredItem._getCursor(p);
            }
        }
        else if (this.hoveredItem) {
            cursor = this.hoveredItem._getCursor(p);
        }
        element.style.cursor = cursor || Cursors.arrow;
    }
    /** @hidden */
    _connectionManipulation(connection, disabledShape, isNew) {
        this.activeConnection = connection;
        this.disabledShape = disabledShape;
        if (isNew) {
            this.newConnection = this.activeConnection;
        }
        else {
            this.newConnection = undefined;
        }
    }
    /** @hidden */
    _updateHoveredItem(point, meta, nativeEvent) {
        const hit = this._hitTest(point);
        const diagram = this.diagram;
        if (hit !== this.hoveredItem && (!this.disabledShape || hit !== this.disabledShape)) {
            if (this.hoveredItem) {
                diagram.trigger(MOUSE_LEAVE, { item: this.hoveredItem, nativeEvent, point, meta });
                this.hoveredItem._hover(false);
            }
            if (hit && hit.options.enable) {
                diagram.trigger(MOUSE_ENTER, { item: hit, nativeEvent, point, meta });
                this.hoveredItem = hit; // Shape, connection or connector
                this.hoveredItem._hover(true);
            }
            else {
                this.hoveredItem = undefined;
            }
        }
    }
    /** @hidden */
    _removeHover() {
        if (this.hoveredItem) {
            this.hoveredItem._hover(false);
            this.hoveredItem = undefined;
        }
    }
    /** @hidden */
    _hitTest(point) {
        const d = this.diagram;
        let hit, item, i;
        // connectors
        if (this._hoveredConnector) {
            this._hoveredConnector._hover(false);
            this._hoveredConnector = undefined;
        }
        if (d._connectorsAdorner._visible) {
            hit = d._connectorsAdorner._hitTest(point);
            if (hit) {
                return hit;
            }
        }
        hit = this.diagram._resizingAdorner._hitTest(point);
        if (hit) {
            this.hoveredAdorner = d._resizingAdorner;
            if (hit.x !== 0 || hit.y !== 0) { // hit testing for resizers or rotator, otherwise if (0,0) than pass through.
                return;
            }
            hit = undefined;
        }
        else {
            this.hoveredAdorner = undefined;
        }
        if (!this.activeTool || this.activeTool.type !== 'ConnectionTool') {
            const selectedConnections = []; // only the connections should have higher presence because the connection edit point is on top of connector.
            // TODO: This should be reworked. The connection adorner should be one for all selected connections and should be hit tested prior the connections and shapes itself.
            for (i = 0; i < d._selectedItems.length; i++) {
                item = d._selectedItems[i];
                if (item instanceof Connection) {
                    selectedConnections.push(item);
                }
            }
            hit = this._hitTestItems(selectedConnections, point);
        }
        return hit || this._hitTestElements(point);
    }
    /** @hidden */
    _hitTestElements(point) {
        const diagram = this.diagram;
        const shapeHit = this._hitTestItems(diagram.shapes, point);
        const connectionHit = this._hitTestItems(diagram.connections, point);
        let hit;
        if ((!this.activeTool || this.activeTool.type !== 'ConnectionTool') && shapeHit && connectionHit && !hitTestShapeConnectors(shapeHit, point)) {
            const mainLayer = diagram.mainLayer;
            const shapeIdx = inArray(shapeHit.visual, mainLayer.children);
            const connectionIdx = inArray(connectionHit.visual, mainLayer.children);
            hit = shapeIdx > connectionIdx ? shapeHit : connectionHit;
        }
        return hit || shapeHit || connectionHit;
    }
    /** @hidden */
    _hitTestItems(array, point) {
        let i, item, hit;
        for (i = array.length - 1; i >= 0; i--) {
            item = array[i];
            hit = item._hitTest(point);
            if (hit) {
                return hit;
            }
        }
    }
}
